import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { Elements as StripeElements } from '@stripe/react-stripe-js';
import { useQuery } from 'react-apollo';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

import RegistrationForm from './components/RegistrationForm';
import ShutterbugPhoneVerify from './components/ShutterbugPhoneVerify';
import RegistrationPayment from './components/RegistrationPayment';
import useStripePromise from '../../../../hooks/useStripePromise';
import useShutterbugRegistration, {
  REGISTRATION_STEPS,
} from './useShutterbugRegistration';
import SignupAlbumLayout from '../../../../components/global/SignupAlbumLayout';
import { getPhotoSearchCheckoutSearchItemsQuery } from '../../../temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import PlanSelection from './components/PlanSelection';
import { isValidEmail } from '../../../../util/email';
import { formatPhone, isValidPhone } from '../../../../util/phone';
import RegistrationEmailSent from './components/RegistrationEmailSent';
import ShutterbugEmailPinVerify from './components/ShutterbugEmailPinVerify';
import EventSKUSelection from './components/EventSKUSelection';
import PressLogos from '../Login/components/PressLogos';
import useAuthenticated from '../../../../hooks/useAuthenticated';
import WaldoSpeechBubble from '../../../../components/global/WaldoSpeechBubble';
import RegistrationImageCarousel from './components/RegistrationImageCarousel';

const Registration = () => {
  const history = useHistory();
  const stripePromise = useStripePromise();
  const authenticated = useAuthenticated();
  const location = useLocation();
  const { data: { searchSubscriptionItems = [] } = {} } = useQuery(
    getPhotoSearchCheckoutSearchItemsQuery,
  );
  const { phone_number: phoneNumber, email_address: emailAddress } = qs.parse(
    location?.search,
  );
  const {
    plan,
    setPlan,
    step,
    setStep,
    loading,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onCheckout,
    onEmailRegistrationRequest,
    onSendEmailVerification,
    onEmailPinVerify,
    onEmailAuthenticationRequest,
    onEventSKUSelected,
    eventType,
    paidPlansOnly,
    requireHumanInput,
    blockLogin,
  } = useShutterbugRegistration();

  useEffect(() => {
    if (authenticated) {
      history.replace('/app');
    }
  }, []);

  const onSubmit = values => {
    if (step === REGISTRATION_STEPS.EVENT_SKU_SELECTION) {
      onEventSKUSelected(values);
    }

    if (
      step === REGISTRATION_STEPS.DETAILS &&
      isValidPhone(values.phone_number)
    ) {
      onPhoneVerificationRequest(values);
    } else if (
      step === REGISTRATION_STEPS.DETAILS &&
      isValidEmail(values.email_address, true)
    ) {
      onEmailRegistrationRequest(values);
    }

    if (step === REGISTRATION_STEPS.PIN_VERIFY) {
      onPinVerify(values);
    }

    if (step === REGISTRATION_STEPS.EMAIL_PIN_VERIFY) {
      onEmailPinVerify(values);
    }

    if (step === REGISTRATION_STEPS.CHECKOUT) {
      onCheckout(values);
    }
  };

  const onValidate = values => {
    const errors = {};

    switch (step) {
      case REGISTRATION_STEPS.DETAILS: {
        if (!isValidEmail(values.email_address, true)) {
          errors.email_address = 'Email address is not valid';
        }

        if (
          values.email_address &&
          isValidEmail(values.email_address, true) &&
          values.email_address.match(/\.con(\..*)?$/)
        ) {
          errors.email_address =
            '.con is not a valid domain. Did you mean .com?';
        }

        if (
          values.phone_number.replace('+1', '').trim().length > 0 &&
          !isValidPhone(values.phone_number)
        ) {
          errors.phone_number = 'Phone number is not valid';
        }
        break;
      }
      default:
        break;
    }
    return errors;
  };

  return (
    <StripeElements stripe={stripePromise}>
      <Helmet>
        <title>Waldo &ndash; Register</title>
      </Helmet>
      <SignupAlbumLayout.Wrapper
        contentClassName="registration-layout"
        imageContent={<RegistrationImageCarousel />}
        imageContainerClassName="registration-image-panel"
      >
        <div className="registration-wrapper">
          {step === REGISTRATION_STEPS.PLAN ? (
            <PlanSelection
              searchSubscriptionItems={searchSubscriptionItems}
              onPlanSelected={subscriptionItem => {
                setPlan(subscriptionItem.plan);
                setStep(REGISTRATION_STEPS.DETAILS);
              }}
            />
          ) : (
            <Formik
              onSubmit={onSubmit}
              initialValues={{
                first_name: '',
                last_name: '',
                email_address: emailAddress || '',
                email_address_exists: false,
                phone_number: phoneNumber || '+1',
                code: '',
                terms_checked: true,

                // Checkout logic
                stripe_token: null,
                plan,
                plan_id: null,
                eventAddonItems: [],
                selectedEventAddonItemIds: [],
                eventItemId: '',
                force_allow_action: false,
              }}
              validate={onValidate}
              validateOnBlur={false}
              validateOnChange={false}
              render={({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                errors,
                touched,
              }) => {
                return (
                  <div className="shutterbug-registration-form-wrapper">
                    {step === REGISTRATION_STEPS.EVENT_SKU_SELECTION && (
                      <EventSKUSelection
                        eventType={eventType}
                        paidPlansOnly={paidPlansOnly}
                        onConfirmSelection={({
                          eventItem,
                          eventAddonItems,
                          selectedEventAddonItemIds,
                        }) => {
                          if (eventItem) {
                            handleChange({
                              target: {
                                name: 'eventItemId',
                                value: eventItem.id,
                              },
                            });
                          } else {
                            handleChange({
                              target: {
                                name: 'selectedEventAddonItemIds',
                                value: selectedEventAddonItemIds,
                              },
                            });
                            handleChange({
                              target: {
                                name: 'eventAddonItems',
                                value: eventAddonItems,
                              },
                            });
                          }

                          handleSubmit();
                        }}
                      />
                    )}
                    {step === REGISTRATION_STEPS.DETAILS && (
                      <>
                        <WaldoSpeechBubble
                          className="w-100-i mx-0 mb-3 registration-details-bubble"
                          position="right"
                        >
                          Hey there! Let&apos;s get your account created.
                        </WaldoSpeechBubble>
                        <RegistrationForm
                          values={values}
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          loading={loading}
                          requireHumanInput={requireHumanInput}
                          blockLogin={blockLogin}
                        />
                      </>
                    )}

                    {step === REGISTRATION_STEPS.PIN_VERIFY && (
                      <div className="signup-album-create">
                        <WaldoSpeechBubble
                          className="w-100-i mx-0 mb-3 registration-details-bubble"
                          position="right"
                        >
                          Please enter the code sent to:{' '}
                          <span className="text-primary">
                            {formatPhone(values.phone_number)}!
                          </span>
                        </WaldoSpeechBubble>
                        <ShutterbugPhoneVerify
                          loading={loading}
                          className="signup-album-create-pin-step"
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          errors={errors}
                          onPinCodeResend={onPinCodeResend}
                          onEmailSignup={() =>
                            onEmailRegistrationRequest(values)
                          }
                          values={values}
                          requireHumanInput={requireHumanInput}
                          blockLogin={blockLogin}
                        />
                      </div>
                    )}
                    {step === REGISTRATION_STEPS.EMAIL_PIN_VERIFY && (
                      <div className="signup-album-create">
                        <WaldoSpeechBubble
                          className="w-100-i mx-0 mb-3 registration-details-bubble"
                          position="right"
                        >
                          Please enter the code sent to:{' '}
                          <span className="text-primary">
                            {values.email_address}
                          </span>
                        </WaldoSpeechBubble>
                        <ShutterbugEmailPinVerify
                          className="signup-album-create-pin-step"
                          loading={loading}
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          errors={errors}
                          onPinCodeResend={() =>
                            onEmailAuthenticationRequest({
                              emailAddress: values.email_address,
                            })
                          }
                          values={values}
                        />
                      </div>
                    )}
                    {step === REGISTRATION_STEPS.VERIFICATION_EMAIL && (
                      <>
                        <h3>Email verification sent.</h3>
                        <RegistrationEmailSent
                          values={values}
                          loading={loading}
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          onSendEmailVerification={onSendEmailVerification}
                          onEditEmail={() =>
                            setStep(REGISTRATION_STEPS.DETAILS)
                          }
                        />
                        <hr />
                        <p className="mb-4 mt-4 text-sm">
                          Need Help?{' '}
                          <a href="mailto:support@waldophotos.com">
                            Contact support &rarr;
                          </a>
                        </p>
                      </>
                    )}

                    {step === REGISTRATION_STEPS.CHECKOUT && (
                      <>
                        <RegistrationPayment
                          values={values}
                          submitting={loading}
                          onCheckout={onSubmit}
                          handleChange={handleChange}
                        />
                        <hr />
                        <p className="mb-4 mt-4 text-sm">
                          Need Help?{' '}
                          <a href="mailto:support@waldophotos.com">
                            Contact support &rarr;
                          </a>
                        </p>
                      </>
                    )}
                  </div>
                );
              }}
            />
          )}
          <div className="registration-press-logos">
            <PressLogos />
          </div>
        </div>
      </SignupAlbumLayout.Wrapper>
    </StripeElements>
  );
};

export default Registration;
